import React, { useEffect, useState } from 'react';
import { Check, CheckCheck, Reply } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../ui/context-menu';
import { Progress } from '../../ui/progress';
import { formatMessageBubbleTime } from '../../../utils/formatChatDateSeparator';
import { getCrmChatAssetUrl } from '../../../utils/api';
import { getTokenSync } from '../../../utils/authStorage';
import { isMobileContainer } from '../../../utils/device';
import useSwipeToReply from '../../../hooks/useSwipeToReply';
import { firstUrlInBody } from '../../../utils/crmChatLinkify';
import CrmChatReplyQuote from './CrmChatReplyQuote';
import CrmChatFileAttachment from './CrmChatFileAttachment';
import CrmChatMessageText from './CrmChatMessageText';
import CrmChatLinkPreview from './CrmChatLinkPreview';

function AuthenticatedImage({ message, onOpen, isMine }) {
  const [src, setSrc] = useState(null);
  const thumbUrl = getCrmChatAssetUrl(message.thumbnail_url || message.attachment_url);

  useEffect(() => {
    if (!thumbUrl) return undefined;
    let cancelled = false;
    const token = getTokenSync();
    fetch(thumbUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        if (!cancelled) setSrc(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (!cancelled) setSrc(thumbUrl);
      });
    return () => {
      cancelled = true;
      if (src?.startsWith('blob:')) URL.revokeObjectURL(src);
    };
  }, [thumbUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!thumbUrl) return null;

  return (
    <button type="button" onClick={onOpen} className="block w-full max-w-[240px] rounded-lg overflow-hidden">
      {src ? (
        <img src={src} alt="" className="w-full h-auto object-cover max-h-48" />
      ) : (
        <div className={`h-32 w-full animate-pulse ${isMine ? 'bg-emerald-700/50' : 'bg-muted'}`} />
      )}
    </button>
  );
}

export default function CrmChatMessageBubble({
  message,
  highlighted = false,
  uploadProgress = 0,
  onReply,
  onScrollToReply,
  onOpenImage,
  registerRef,
}) {
  const isMine = message.is_mine;
  const time = formatMessageBubbleTime(message.created_at);
  const isMobile = isMobileContainer();
  const { swipeHandlers, bindSwipeEnd } = useSwipeToReply(onReply);
  const isPending = message._pending;
  const previewUrl = firstUrlInBody(message.body);

  const bubble = (
    <div
      ref={(el) => registerRef?.(message.id, el)}
      className={`flex ${isMine ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-1 duration-200`}
    >
      <div className="relative group max-w-[78%]">
        {!isMobile && (
          <button
            type="button"
            onClick={() => onReply?.(message)}
            className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity z-10 h-7 w-7 rounded-full bg-card border border-border shadow flex items-center justify-center ${
              isMine ? '-left-9' : '-right-9'
            }`}
            aria-label="Reply"
          >
            <Reply className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}

        <div
          {...(isMobile ? swipeHandlers : {})}
          onPointerUp={isMobile ? (e) => bindSwipeEnd(e, message) : undefined}
          className={`px-3 py-2 rounded-2xl text-sm shadow-sm touch-pan-y transition-shadow ${
            highlighted ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-background' : ''
          } ${
            isMine
              ? 'bg-emerald-600 text-white rounded-br-md'
              : 'bg-muted text-foreground rounded-bl-md'
          }`}
        >
          <CrmChatReplyQuote
            replyTo={message.reply_to}
            isMine={isMine}
            onClick={onScrollToReply}
          />

          {message.message_type === 'image' && (
            <div className="mb-1">
              <AuthenticatedImage message={message} onOpen={() => onOpenImage?.(message)} isMine={isMine} />
            </div>
          )}

          {message.message_type === 'file' && (
            <div className="mb-1">
              <CrmChatFileAttachment message={message} isMine={isMine} />
            </div>
          )}

          {message.body && (
            <>
              <p className="whitespace-pre-wrap break-words">
                <CrmChatMessageText body={message.body} isMine={isMine} />
              </p>
              {previewUrl && <CrmChatLinkPreview url={previewUrl} isMine={isMine} />}
            </>
          )}

          {isPending && uploadProgress > 0 && (
            <div className="mt-2">
              <Progress value={uploadProgress} className="h-1" />
            </div>
          )}

          <div
            className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${
              isMine ? 'text-emerald-100/90' : 'text-muted-foreground'
            }`}
          >
            <span>{time}</span>
            {isMine && !isPending && (
              <span className="inline-flex" aria-label={message.is_read ? 'Read' : 'Sent'}>
                {message.is_read ? (
                  <CheckCheck className="h-3 w-3" />
                ) : (
                  <Check className="h-3 w-3" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return bubble;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{bubble}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onClick={() => onReply?.(message)}>Reply</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
